import React, { useState, useEffect } from 'react';
import { X, Clock, Play, Pause, Square, Plus, Trash2, Calendar } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface TimeTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  taskId: string | null;
  taskTitle?: string;
}

interface TimeEntry {
  id: string;
  minutes: number;
  description: string;
  date: string;
  user: string;
}

interface TimerState {
  isRunning: boolean;
  startTime: number | null;
  elapsedTime: number;
}

export const TimeTrackingModal: React.FC<TimeTrackingModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  taskId,
  taskTitle
}) => {
  const [loading, setLoading] = useState(false);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [newEntry, setNewEntry] = useState({ minutes: '', description: '', date: new Date().toISOString().split('T')[0] });
  const [timer, setTimer] = useState<TimerState>({ isRunning: false, startTime: null, elapsedTime: 0 });
  const [totalTime, setTotalTime] = useState(0);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTimeEntries();
    }
  }, [isOpen, taskId]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timer.isRunning && timer.startTime) {
      interval = setInterval(() => {
        setTimer(prev => ({
          ...prev,
          elapsedTime: Date.now() - (prev.startTime || 0)
        }));
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer.isRunning, timer.startTime]);

  const fetchTimeEntries = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      // This would be a new endpoint to get time entries for a task
      const response = await api.get(`/firm-admin/tasks/${taskId}/time-entries`);
      if (response.data.success) {
        setTimeEntries(response.data.data.entries || []);
        setTotalTime(response.data.data.totalMinutes || 0);
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
      // For now, we'll just show empty state
      setTimeEntries([]);
      setTotalTime(0);
    } finally {
      setLoading(false);
    }
  };

  const startTimer = () => {
    setTimer({
      isRunning: true,
      startTime: Date.now(),
      elapsedTime: 0
    });
  };

  const stopTimer = async () => {
    if (!timer.startTime || !taskId) return;

    const minutes = Math.round(timer.elapsedTime / 60000);
    if (minutes > 0) {
      try {
        const response = await api.post(`/firm-admin/tasks/${taskId}/time`, {
          minutes,
          description: `Timer session - ${formatTime(timer.elapsedTime)}`
        });

        if (response.data.success) {
          toast.success(`Added ${minutes} minutes to task`);
          fetchTimeEntries();
          onSuccess();
        }
      } catch (error: any) {
        console.error('Error adding timer time:', error);
        toast.error('Failed to add time from timer');
      }
    }

    setTimer({ isRunning: false, startTime: null, elapsedTime: 0 });
  };

  const addManualEntry = async () => {
    if (!taskId || !newEntry.minutes) return;

    setLoading(true);
    try {
      const response = await api.post(`/firm-admin/tasks/${taskId}/time`, {
        minutes: parseInt(newEntry.minutes),
        description: newEntry.description,
        date: newEntry.date
      });

      if (response.data.success) {
        toast.success('Time entry added successfully');
        setNewEntry({ minutes: '', description: '', date: new Date().toISOString().split('T')[0] });
        fetchTimeEntries();
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error adding time entry:', error);
      toast.error('Failed to add time entry');
    } finally {
      setLoading(false);
    }
  };

  const deleteTimeEntry = async (entryId: string) => {
    if (!confirm('Are you sure you want to delete this time entry?')) return;

    try {
      // This would be a new endpoint to delete time entries
      const response = await api.delete(`/firm-admin/tasks/${taskId}/time-entries/${entryId}`);
      if (response.data.success) {
        toast.success('Time entry deleted');
        fetchTimeEntries();
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error deleting time entry:', error);
      toast.error('Failed to delete time entry');
    }
  };

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Time Tracking</h2>
            {taskTitle && (
              <p className="text-sm text-gray-600 mt-1">{taskTitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Timer Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Timer</h3>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-mono font-bold text-gray-900">
                {formatTime(timer.elapsedTime)}
              </div>
              <div className="flex gap-2">
                {!timer.isRunning ? (
                  <button
                    onClick={startTimer}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Timer
                  </button>
                ) : (
                  <button
                    onClick={stopTimer}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Square className="w-4 h-4 mr-2 fill-current" />
                    Stop & Save
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Manual Entry Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Manual Entry</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minutes
                </label>
                <input
                  type="number"
                  min="1"
                  value={newEntry.minutes}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, minutes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="60"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Work description"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={addManualEntry}
                  disabled={!newEntry.minutes || loading}
                  className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Entry
                </button>
              </div>
            </div>
          </div>

          {/* Time Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Total Time Logged</span>
              </div>
              <span className="text-2xl font-bold text-blue-900">
                {formatMinutes(totalTime)}
              </span>
            </div>
          </div>

          {/* Time Entries List */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Time Entries</h3>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
              </div>
            ) : timeEntries.length > 0 ? (
              <div className="space-y-3">
                {timeEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <span className="font-medium text-gray-900">
                          {formatMinutes(entry.minutes)}
                        </span>
                        <span className="text-sm text-gray-600">
                          {new Date(entry.date).toLocaleDateString()}
                        </span>
                        <span className="text-sm text-gray-500">
                          by {entry.user}
                        </span>
                      </div>
                      {entry.description && (
                        <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteTimeEntry(entry.id)}
                      className="text-gray-400 hover:text-red-600"
                      title="Delete entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>No time entries yet</p>
                <p className="text-sm">Start the timer or add a manual entry to begin tracking time</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-4 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
